import { DropdownListItem } from '@design/components/dropdown/dropdown-list-item/DropdownListItem';
import { Icon } from '@design/components/icon/Icon';
import { ReactNode } from 'react';

import { MentionCandidate } from '../types';
import styles from './MentionPopup.module.scss';

export interface MentionPopupSection {
  label: string;
  items: MentionCandidate[];
}

export interface MentionSectionTitleRow {
  title: string;
  disabled: true;
}

export type MentionDropdownItem = MentionSectionTitleRow | MentionCandidate;

export const isMentionSectionTitleRow = (
  item: MentionDropdownItem | undefined
): item is MentionSectionTitleRow => !!item && 'title' in item;

export const flattenMentionSections = (
  sections: MentionPopupSection[]
): MentionDropdownItem[] =>
  sections.flatMap(section =>
    section.items.length === 0
      ? []
      : [{ title: section.label, disabled: true as const }, ...section.items]
  );

const isWatchlistCandidate = (item: MentionCandidate): boolean =>
  item.kind === 'list' || item.kind === 'public-list';

const DefaultMentionSectionTitle = ({ title }: { title: string }) => (
  <div className={styles.sectionTitle}>{title}</div>
);

const DefaultMentionCandidateRow = ({
  item,
  isFocused,
}: {
  item: MentionCandidate;
  isFocused: boolean;
}) => (
  <DropdownListItem
    label={item.label}
    leadingIcon={
      isWatchlistCandidate(item) ? (
        <Icon iconName="Watchlist" size={16} />
      ) : item.avatarUrl ? (
        <img src={item.avatarUrl} alt="" className={styles.avatar} />
      ) : undefined
    }
    isActive={false}
    isFocused={isFocused}
    data-testid={`mention-candidate-${item.id}`}
  />
);

export interface MentionPopupRowProps {
  item: MentionDropdownItem;
  isFocused: boolean;
  searchQuery: string;
  /** Override rendering of a candidate row — e.g. to show a richer, app-specific card. */
  renderCandidate?: (
    item: MentionCandidate,
    isFocused: boolean,
    searchQuery: string
  ) => ReactNode;
  /** Override rendering of a section title row. */
  renderSectionTitle?: (title: string) => ReactNode;
}

export const MentionPopupRow = ({
  item,
  isFocused,
  searchQuery,
  renderCandidate,
  renderSectionTitle,
}: MentionPopupRowProps) => {
  if (isMentionSectionTitleRow(item)) {
    return renderSectionTitle ? (
      <>{renderSectionTitle(item.title)}</>
    ) : (
      <DefaultMentionSectionTitle title={item.title} />
    );
  }

  if (renderCandidate) {
    return <>{renderCandidate(item, isFocused, searchQuery)}</>;
  }

  return <DefaultMentionCandidateRow item={item} isFocused={isFocused} />;
};
